import React, {useContext} from 'react'
import ProductsListSidebar from 'containers/list/products/products_list_sidebar'
import ProductsGridHeader from 'containers/list/products/products_grid_header'
import ProductsGridPaginator from 'containers/list/products/products_grid_paginator'
import productsMachine from 'containers/list/products/products_machine'
import ProductCard from 'containers/list/products/product_card'
import ListProductCard from 'containers/list/products/list_product_card'
import {Col, Row} from 'reactstrap'
import { useMachine } from '@xstate/react'
import noResults from 'img/no-results'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import AppContext from 'utils/app_context'
import {get} from 'lodash'
import AvailabilityGroupWidget from 'containers/home/availability_group_widget'

const CategoriesColumn = styled(Col)`
  flex: 0 0 100%;
  max-width: 100%;

  /* A partir de xl → 22% */
  @media (min-width: 1300px) {
    flex: 0 0 20% !important;
    max-width: 20% !important;
  }
`;

const ProductsColumn = styled(Col)`
  flex: 0 0 100%;
  max-width: 100%;

  @media (min-width: 1300px) {
    flex: ${({ fullSize }) => (fullSize ? '0 0 100%' : '0 0 80%')} !important;
    max-width: ${({ fullSize }) => (fullSize ? '100%' : '80%')} !important;
  }
`;

const ProductList = (props) => {
  const gridStyle = get(props, 'theme.settings.product_list.default_view', 'grid')
  const showSidebar = get(props, 'theme.settings.product_list.show_sidebar', true)
  const sidebarFilters = get(props, 'theme.settings.product_list.sidebar_filters', true)

  const {urls, categories, suppliers, cities, product_tags, product_languages, product_types, product_schedule_types, destination_id, query} = props

  const {mediaBreakpoint} = useContext(AppContext)
  const {i18n} = useTranslation()

  function findCategory(items, key) {
    if (!items) return
    for (const item of items) {
      if (item.key.toString() == key) return item
      const child = findCategory(item.children, key)
      if (child) return child
    }
  }

  const initialCategory = findCategory(categories, query.category)
  const initialSupplier = suppliers.find((c) => c.value.toString() == query.supplier)
  const initialCity = cities.find((c) => c.value.toString() == query.city)

  const initialFilters = () => {
    let filters = []

    if (initialCategory) {
      filters.push({param: 'category', value: initialCategory.key, label: initialCategory.title})
    }

    if (initialSupplier) {
      filters.push({param: 'supplier', value: initialSupplier.value, label: initialSupplier.label})
    }

    if (initialCity) {
      filters.push({param: 'city', value: initialCity.value, label: initialCity.label})
    }

    return filters
  }

  const machine = useMachine(productsMachine, {
    context: {
      endpoint: urls.grid,
      pagination: {current_page: query.page},
      filters: initialFilters()
    }
  })

  const [current, send] = machine
  const {context} = current
  const {filters, pagination, products} = context

  const ProductGridView = ({products}) => (
    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-3 p-0">
      {products.map((product, key) => (
        <ProductCard
          {...product}
          machine={machine}
          key={key}
          urls={urls}
          url={urls.service.replace(/ *\:[^)]*\: */g, product.slug)}
        />
      ))}
    </div>
  )

  const ProductListView = () => (
    <div className="row">
      {products.map((product, key) => (
        <Col xs="12" className="mx-0 p-0 mb-5" key={key}>
          <ListProductCard
            {...product}
            machine={machine}
            urls={urls}
            url={urls.service.replace(/ *\:[^)]*\: */g, product.slug)}
          />
        </Col>
      ))}
    </div>
  )

  return (
    <Row>
      {showSidebar &&
        <CategoriesColumn xs="12" lg={showSidebar ? "3" : "12"} mediaBreakpoint={mediaBreakpoint}>
          <ProductsListSidebar
            theme={props.theme}
            machine={machine}
            sidebarFilters={sidebarFilters}
            categories={categories}
            suppliers={suppliers}
            cities={cities}
            productTags={product_tags}
            productLanguages={product_languages}
            productTypes={product_types}
            productScheduleTypes={product_schedule_types}
            initialCategory={initialCategory}
            filterCities={props.destination.filter_cities}
          />
        </CategoriesColumn>
      }

      <ProductsColumn xs="12" lg={showSidebar ? "9" : "12"} mediaBreakpoint={mediaBreakpoint} fullSize={!showSidebar}>
        {destination_id == 'barcelona' &&
          <AvailabilityGroupWidget {...props} variant="banner" fetchFirstDay={true}/>
        }

        <ProductsGridHeader
          destination_id={destination_id}
          filters={filters}
          pagination={pagination}
          onRemoveFilter={(e) => send({type: 'FILTER_REMOVE', param: e.currentTarget.dataset.param})}
          onRemoveAllFilters={() => send({type: 'FILTER_REMOVE_ALL'})}
        />

        {products.length > 0 ? (
          <div className="px-2">
            {gridStyle == 'grid' ? (
              <ProductGridView products={products}/>
            ) : (
              <ProductListView products={products}/>
            )}
          </div>
        ) : (
          <div className="alert alert-warning mb-4 d-flex justify-content-start" role="alert">
            <div>
              <img width="110" height="110" className="me-4" src={noResults} alt="no results" />
            </div>
            <div>
              <h4 className="alert-heading">{i18n.t('product_list.without_results_alert_title')}</h4>
              <hr/>
              <p className="m-0">{i18n.t('product_list.without_results_alert_content')}</p>
            </div>
          </div>
        )}

        {products.length > 0 &&
          <div className="d-flex flex-column flex-md-row justify-content-between p-2 mt-3">
            <span className="p-2 bg-white rounded text-primary order-2 text-center">
              {i18n.t('product_list.total_products', {count: pagination.total_items})}
            </span>
            <div className="d-flex justify-content-center order-0 order-md-1">
              <ProductsGridPaginator
                {...pagination}
                onPageSelected={(page) => send({type: 'PAGINATION_SELECTION', page})}
              />
            </div>
            <span className="my-3 my-md-0 p-2 bg-white rounded text-primary order-0 order-md-0 text-center">
              {i18n.t('product_list.current_page', {page: pagination.current_page})}
            </span>
          </div>
        }
      </ProductsColumn>
    </Row>
  )
}

export default ProductList
